import type { DocumentCommentModel } from "@/api/cle-api";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import Spinner from "@/components/spinner/Spinner";
import { useEffect, useRef, useState } from "react";

import { REPORTED_EVIDENCE_LOCKED_REASON } from "./DocumentClassificationActions";
import { formatCommentAuthor, formatDateTime } from "./utils/formatters";

interface CourseCertificateCommentsProps {
  comments: DocumentCommentModel[];
  error: Error | null;
  isAddingComment: boolean;
  isLoading: boolean;
  onAddComment: (content: string) => void;
  readOnly?: boolean;
}

export const CourseCertificateComments = ({
  comments,
  error,
  isAddingComment,
  isLoading,
  onAddComment,
  readOnly = false,
}: CourseCertificateCommentsProps) => {
  const [comment, setComment] = useState("");

  const handleSubmit = () => {
    const trimmedComment = comment.trim();
    if (!trimmedComment) return;

    onAddComment(trimmedComment);
    setComment("");
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,

      behavior: "smooth",
    });
  }, [comments.length]);
  return (
    <div className="c-card">
      <h3 className="mb-3">Besked til sekretariatet</h3>
      {isLoading && <Spinner label="Henter beskeder ..." />}
      {error && (
        <CleApiErrorText
          error={error}
          fallback="Kunne ikke hente beskeder."
          as="p"
        />
      )}
      {!isLoading && !error && comments.length === 0 && (
        <p className="text-muted text-center">
          {readOnly
            ? "Ingen beskeder."
            : "Ingen beskeder endnu. Skriv den første besked herunder."}
        </p>
      )}
      {!isLoading && !error && comments.length > 0 && (
        <div
          ref={containerRef}
          className="d-flex flex-column gap-3 mb-3"
          style={{ maxHeight: "32rem", overflowY: "auto" }}
        >
          {comments.map((item, index) => (
            <div
              className="border rounded p-3"
              key={String(item.id ?? `${item.createdAt}-${index}`)}
            >
              <div className="d-flex justify-content-between gap-3">
                <strong>{formatCommentAuthor(item)}</strong>
                <span className="text-muted">
                  {formatDateTime(item.createdAt)}
                </span>
              </div>
              <div>{item.content}</div>
            </div>
          ))}
        </div>
      )}
      {readOnly ? (
        <p className="text-muted mb-0">{REPORTED_EVIDENCE_LOCKED_REASON}</p>
      ) : (
        // Sekretariatet skal kunne starte samtalen, så feltet vises også,
        // før der er nogen beskeder.
        !isLoading &&
        !error && (
          <>
            <textarea
              aria-label="Tilføj kommentar"
              className="form-control mb-2"
              disabled={isAddingComment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Tilføj en kommentar..."
              rows={4}
              value={comment}
            />
            <Button
              className="w-100"
              color="secondary"
              disabled={!comment.trim() || isAddingComment}
              onClick={handleSubmit}
              type="button"
            >
              {isAddingComment ? <Spinner size={14} /> : null}
              Tilføj Kommentar
            </Button>
          </>
        )
      )}
    </div>
  );
};
